import React, { useState, useRef } from 'react';
import { ArrowLeft, Search, CheckCircle, XCircle, Phone, ChevronDown, Info, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { carModels, advisorInfo } from '../data/mock';

// ──── ELIGIBILITY DATA ────
const eligibilityData = {
  1500: ['Bezza G'],
  1600: ['Axia G', 'Bezza G'],
  1700: ['Axia G', 'Axia X', 'Bezza G', 'Bezza X'],
  1800: ['Axia G', 'Axia X', 'Bezza G', 'Bezza X', 'Myvi G'],
  1900: ['Axia G', 'Axia X', 'Axia SE', 'Bezza G', 'Bezza X', 'Myvi G', 'Myvi X'],
  2000: ['Axia G', 'Axia X', 'Axia SE', 'Axia AV', 'Bezza G', 'Bezza X', 'Bezza AV', 'Myvi G', 'Myvi X', 'Myvi H', 'Myvi AV', 'Ativa X', 'Alza X'],
  2100: ['Axia G', 'Axia X', 'Axia SE', 'Axia AV', 'Bezza G', 'Bezza X', 'Bezza AV', 'Myvi G', 'Myvi X', 'Myvi H', 'Myvi AV', 'Ativa X', 'Ativa H', 'Alza X', 'Alza H'],
  2200: ['Axia G', 'Axia X', 'Axia SE', 'Axia AV', 'Bezza G', 'Bezza X', 'Bezza AV', 'Myvi G', 'Myvi X', 'Myvi H', 'Myvi AV', 'Ativa X', 'Ativa H', 'Ativa AV', 'Alza X', 'Alza H', 'Alza AV', 'Aruz X', 'Traz X'],
  2300: ['Axia G', 'Axia X', 'Axia SE', 'Axia AV', 'Bezza G', 'Bezza X', 'Bezza AV', 'Myvi G', 'Myvi X', 'Myvi H', 'Myvi AV', 'Ativa X', 'Ativa H', 'Ativa AV', 'Alza X', 'Alza H', 'Alza AV', 'Aruz X', 'Traz X'],
  2400: ['Axia G', 'Axia X', 'Axia SE', 'Axia AV', 'Bezza G', 'Bezza X', 'Bezza AV', 'Myvi G', 'Myvi X', 'Myvi H', 'Myvi AV', 'Ativa X', 'Ativa H', 'Ativa AV', 'Alza X', 'Alza H', 'Alza AV', 'Aruz X', 'Aruz AV', 'Traz X', 'Traz H'],
};

const variantMap = {
  axia: { G: '1.0 G', X: '1.0 X', SE: '1.0 SE', AV: '1.0 AV' },
  bezza: { G: '1.0 G (AT)', X: '1.3 X (AT)', AV: '1.3 AV (AT)' },
  myvi: { G: '1.3 G', X: '1.5 X', H: '1.5 H', AV: '1.5 AV' },
  ativa: { X: '1.0 X', H: '1.0 H', AV: '1.0 AV' },
  alza: { X: '1.5 X', H: '1.5 H', AV: '1.5 AV' },
  aruz: { X: '1.5 X', AV: '1.5 AV' },
  traz: { X: '1.5 X (CVT)', H: '1.5 H (CVT)' },
};

const allModelIds = ['axia', 'bezza', 'myvi', 'ativa', 'alza', 'aruz', 'traz'];
const modelDisplayNames = { axia: 'AXIA', bezza: 'BEZZA', myvi: 'MYVI', ativa: 'ATIVA', alza: 'ALZA', aruz: 'ARUZ', traz: 'TRAZ' };

// ──── THEMES ────
const themes = {
  blue: {
    name: 'Midnight Blue',
    dot: 'bg-blue-600',
    page: 'bg-gradient-to-b from-[#0A1628] via-[#0F2440] to-[#0A1628]',
    header: 'bg-[#0A1628]/90 border-white/[0.06]',
    headerText: 'text-white/60 hover:text-white',
    headerSub: 'text-white/40',
    badge: 'bg-blue-500/15 border-blue-500/20 text-blue-400',
    h1: 'text-white',
    h1Accent: 'from-blue-400 to-cyan-400',
    subtitle: 'text-white/50',
    card: 'bg-white/[0.05] border-white/[0.08]',
    label: 'text-white/70',
    input: 'bg-white/[0.06] border-white/[0.1] text-white placeholder:text-white/20 focus:border-blue-500/50 focus:ring-blue-500/30',
    inputPrefix: 'text-white/40',
    btn: 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white',
    note: 'text-white/30',
    eligible: 'border-green-500/40 from-green-500/[0.08] to-green-600/[0.04]',
    eligibleText: 'text-green-400',
    eligibleChip: 'bg-green-500/20 text-green-300 border-green-500/20',
    notEligible: 'border-red-500/20 from-red-500/[0.05] to-red-600/[0.02]',
    notEligibleText: 'text-red-400',
    modelName: 'text-white',
    variantCount: 'text-white/50',
    chevron: 'text-white/40',
    tableHeader: 'bg-white/[0.06] text-white/50',
    tableRow: 'bg-white/[0.02]',
    tableText: 'text-white/80',
    tablePrice: 'text-white',
    tableMonthly: 'text-green-400',
    tableNote: 'text-white/30',
    notEligibleSub: 'text-red-400/60',
    summary: 'from-blue-600/15 to-cyan-600/15 border-blue-500/20',
    summaryText: 'text-white/60',
    summaryBold: 'text-white',
    summaryChip: 'bg-green-500/20 text-green-400 border-green-500/20',
    cta: 'from-white/[0.06] to-white/[0.02] border-white/[0.08]',
    ctaTitle: 'text-white',
    ctaSub: 'text-white/50',
    ctaBtn: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    belowMin: 'bg-red-500/10 border-red-500/20',
    belowMinTitle: 'text-white',
    belowMinSub: 'text-white/50',
    belowMinNote: 'text-white/40',
    disclaimer: 'text-white/20',
    footer: 'border-white/[0.06]',
    footerText: 'text-white/20',
    themeSelectorBg: 'bg-white/[0.05] border-white/[0.08]',
    themeSelectorLabel: 'text-white/40',
  },
  red: {
    name: 'Perodua Red',
    dot: 'bg-red-600',
    page: 'bg-gradient-to-b from-[#3B0000] via-[#8B0000] to-[#3B0000]',
    header: 'bg-[#2A0000]/90 border-red-900/30',
    headerText: 'text-white/60 hover:text-white',
    headerSub: 'text-white/40',
    badge: 'bg-amber-500/15 border-amber-500/30 text-amber-400',
    h1: 'text-white',
    h1Accent: 'from-amber-400 to-yellow-300',
    subtitle: 'text-white/50',
    card: 'bg-black/30 border-red-800/30',
    label: 'text-white/70',
    input: 'bg-white/[0.06] border-red-700/30 text-white placeholder:text-white/20 focus:border-amber-500/50 focus:ring-amber-500/30',
    inputPrefix: 'text-white/40',
    btn: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold',
    note: 'text-white/30',
    eligible: 'border-green-500/40 from-green-500/[0.08] to-green-600/[0.04]',
    eligibleText: 'text-green-400',
    eligibleChip: 'bg-green-500/20 text-green-300 border-green-500/20',
    notEligible: 'border-red-800/40 from-red-900/20 to-red-950/10',
    notEligibleText: 'text-red-400',
    modelName: 'text-white',
    variantCount: 'text-white/50',
    chevron: 'text-white/40',
    tableHeader: 'bg-white/[0.06] text-white/50',
    tableRow: 'bg-white/[0.02]',
    tableText: 'text-white/80',
    tablePrice: 'text-white',
    tableMonthly: 'text-amber-400',
    tableNote: 'text-white/30',
    notEligibleSub: 'text-red-400/60',
    summary: 'from-amber-600/15 to-yellow-600/10 border-amber-500/20',
    summaryText: 'text-white/60',
    summaryBold: 'text-white',
    summaryChip: 'bg-amber-500/20 text-amber-400 border-amber-500/20',
    cta: 'from-white/[0.06] to-white/[0.02] border-red-800/30',
    ctaTitle: 'text-white',
    ctaSub: 'text-white/50',
    ctaBtn: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    belowMin: 'bg-red-900/30 border-red-700/30',
    belowMinTitle: 'text-white',
    belowMinSub: 'text-white/50',
    belowMinNote: 'text-white/40',
    disclaimer: 'text-white/20',
    footer: 'border-red-900/30',
    footerText: 'text-white/20',
    themeSelectorBg: 'bg-black/30 border-red-800/20',
    themeSelectorLabel: 'text-white/40',
  },
  white: {
    name: 'Clean White',
    dot: 'bg-slate-300 border border-slate-400',
    page: 'bg-gradient-to-b from-slate-50 via-white to-slate-100',
    header: 'bg-white/95 border-slate-200',
    headerText: 'text-slate-600 hover:text-slate-900',
    headerSub: 'text-slate-400',
    badge: 'bg-blue-50 border-blue-200 text-blue-600',
    h1: 'text-slate-900',
    h1Accent: 'from-blue-600 to-cyan-600',
    subtitle: 'text-slate-500',
    card: 'bg-white border-slate-200 shadow-sm',
    label: 'text-slate-700',
    input: 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/30',
    inputPrefix: 'text-slate-400',
    btn: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white',
    note: 'text-slate-400',
    eligible: 'border-green-200 from-green-50 to-emerald-50',
    eligibleText: 'text-green-600',
    eligibleChip: 'bg-green-100 text-green-700 border-green-200',
    notEligible: 'border-red-200 from-red-50 to-rose-50',
    notEligibleText: 'text-red-500',
    modelName: 'text-slate-900',
    variantCount: 'text-slate-400',
    chevron: 'text-slate-400',
    tableHeader: 'bg-slate-50 text-slate-500',
    tableRow: 'bg-slate-50/50',
    tableText: 'text-slate-600',
    tablePrice: 'text-slate-900',
    tableMonthly: 'text-green-600',
    tableNote: 'text-slate-400',
    notEligibleSub: 'text-red-400',
    summary: 'from-blue-50 to-cyan-50 border-blue-200',
    summaryText: 'text-slate-600',
    summaryBold: 'text-slate-900',
    summaryChip: 'bg-green-100 text-green-700 border-green-200',
    cta: 'from-slate-50 to-white border-slate-200',
    ctaTitle: 'text-slate-900',
    ctaSub: 'text-slate-500',
    ctaBtn: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    belowMin: 'bg-red-50 border-red-200',
    belowMinTitle: 'text-slate-900',
    belowMinSub: 'text-slate-600',
    belowMinNote: 'text-slate-500',
    disclaimer: 'text-slate-400',
    footer: 'border-slate-200',
    footerText: 'text-slate-400',
    themeSelectorBg: 'bg-white border-slate-200 shadow-sm',
    themeSelectorLabel: 'text-slate-400',
  },
  pink: {
    name: 'Rose Pink',
    dot: 'bg-pink-400',
    page: 'bg-gradient-to-b from-[#1A0A14] via-[#2D1028] to-[#1A0A14]',
    header: 'bg-[#1A0A14]/90 border-pink-900/20',
    headerText: 'text-white/60 hover:text-white',
    headerSub: 'text-white/40',
    badge: 'bg-pink-500/15 border-pink-500/25 text-pink-400',
    h1: 'text-white',
    h1Accent: 'from-pink-400 to-rose-300',
    subtitle: 'text-white/50',
    card: 'bg-white/[0.05] border-pink-800/20',
    label: 'text-white/70',
    input: 'bg-white/[0.06] border-pink-700/20 text-white placeholder:text-white/20 focus:border-pink-400/50 focus:ring-pink-400/30',
    inputPrefix: 'text-white/40',
    btn: 'bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white',
    note: 'text-white/30',
    eligible: 'border-emerald-500/40 from-emerald-500/[0.08] to-emerald-600/[0.04]',
    eligibleText: 'text-emerald-400',
    eligibleChip: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/20',
    notEligible: 'border-pink-800/30 from-pink-900/20 to-rose-950/10',
    notEligibleText: 'text-rose-400',
    modelName: 'text-white',
    variantCount: 'text-white/50',
    chevron: 'text-white/40',
    tableHeader: 'bg-white/[0.06] text-white/50',
    tableRow: 'bg-white/[0.02]',
    tableText: 'text-white/80',
    tablePrice: 'text-white',
    tableMonthly: 'text-pink-400',
    tableNote: 'text-white/30',
    notEligibleSub: 'text-rose-400/60',
    summary: 'from-pink-600/15 to-rose-600/10 border-pink-500/20',
    summaryText: 'text-white/60',
    summaryBold: 'text-white',
    summaryChip: 'bg-pink-500/20 text-pink-300 border-pink-500/20',
    cta: 'from-white/[0.06] to-white/[0.02] border-pink-800/20',
    ctaTitle: 'text-white',
    ctaSub: 'text-white/50',
    ctaBtn: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    belowMin: 'bg-rose-500/10 border-rose-500/20',
    belowMinTitle: 'text-white',
    belowMinSub: 'text-white/50',
    belowMinNote: 'text-white/40',
    disclaimer: 'text-white/20',
    footer: 'border-pink-900/20',
    footerText: 'text-white/20',
    themeSelectorBg: 'bg-white/[0.05] border-pink-800/15',
    themeSelectorLabel: 'text-white/40',
  },
  gold: {
    name: 'Gold Premium',
    dot: 'bg-amber-500',
    page: 'bg-gradient-to-b from-[#0A0A0A] via-[#111111] to-[#0A0A0A]',
    header: 'bg-[#0A0A0A]/90 border-amber-900/20',
    headerText: 'text-white/60 hover:text-white',
    headerSub: 'text-amber-600/60',
    badge: 'bg-amber-500/10 border-amber-500/25 text-amber-500',
    h1: 'text-white',
    h1Accent: 'from-amber-400 to-yellow-500',
    subtitle: 'text-white/40',
    card: 'bg-white/[0.03] border-amber-700/20',
    label: 'text-amber-200/70',
    input: 'bg-white/[0.04] border-amber-700/25 text-white placeholder:text-white/20 focus:border-amber-500/50 focus:ring-amber-500/30',
    inputPrefix: 'text-amber-500/50',
    btn: 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold',
    note: 'text-white/25',
    eligible: 'border-amber-600/30 from-amber-500/[0.06] to-yellow-600/[0.03]',
    eligibleText: 'text-amber-400',
    eligibleChip: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
    notEligible: 'border-white/[0.06] from-white/[0.02] to-white/[0.01]',
    notEligibleText: 'text-white/40',
    modelName: 'text-white',
    variantCount: 'text-white/40',
    chevron: 'text-white/30',
    tableHeader: 'bg-white/[0.04] text-amber-500/50',
    tableRow: 'bg-white/[0.015]',
    tableText: 'text-white/70',
    tablePrice: 'text-white',
    tableMonthly: 'text-amber-400',
    tableNote: 'text-white/25',
    notEligibleSub: 'text-white/30',
    summary: 'from-amber-600/10 to-yellow-600/5 border-amber-600/20',
    summaryText: 'text-white/50',
    summaryBold: 'text-amber-400',
    summaryChip: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    cta: 'from-white/[0.04] to-white/[0.02] border-amber-800/20',
    ctaTitle: 'text-white',
    ctaSub: 'text-white/40',
    ctaBtn: 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white',
    belowMin: 'bg-red-500/10 border-red-800/20',
    belowMinTitle: 'text-white',
    belowMinSub: 'text-white/50',
    belowMinNote: 'text-white/40',
    disclaimer: 'text-white/15',
    footer: 'border-amber-900/15',
    footerText: 'text-white/20',
    themeSelectorBg: 'bg-white/[0.03] border-amber-800/15',
    themeSelectorLabel: 'text-amber-600/40',
  },
};

const themeKeys = ['blue', 'red', 'white', 'pink', 'gold'];

// ──── LOGIC ────
const getBracket = (salary) => {
  if (salary < 1500) return null;
  const brackets = Object.keys(eligibilityData).map(Number).sort((a, b) => a - b);
  let matched = brackets[0];
  for (const b of brackets) {
    if (salary >= b) matched = b;
    else break;
  }
  return matched;
};

const getEligibleVariants = (salary) => {
  const bracket = getBracket(salary);
  if (!bracket) return [];
  return eligibilityData[bracket] || [];
};

const getVariantDetails = (modelId, shortVariant) => {
  const fullName = variantMap[modelId]?.[shortVariant];
  if (!fullName) return null;
  const model = carModels.find(m => m.id === modelId);
  if (!model) return null;
  return model.variants.find(v => v.name === fullName) || null;
};

const formatRM = (val) => `RM${val.toLocaleString()}`;

// ──── COMPONENTS ────
const ThemeSelector = ({ current, onChange, t }) => (
  <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${t.themeSelectorBg}`} data-testid="theme-selector">
    <Palette className={`w-3.5 h-3.5 ${t.themeSelectorLabel}`} />
    <span className={`text-[10px] font-semibold uppercase tracking-wider ${t.themeSelectorLabel}`}>Theme</span>
    <div className="flex gap-2">
      {themeKeys.map(key => (
        <button
          key={key}
          onClick={() => onChange(key)}
          data-testid={`theme-${key}`}
          title={themes[key].name}
          className={`w-6 h-6 rounded-full transition-all duration-200 ${themes[key].dot} ${
            current === key ? 'ring-2 ring-offset-2 ring-offset-transparent scale-110 ring-white/60' : 'opacity-60 hover:opacity-100 hover:scale-105'
          }`}
        />
      ))}
    </div>
  </div>
);

const ModelResultCard = ({ modelId, eligibleShortVariants, expanded, onToggle, t }) => {
  const model = carModels.find(m => m.id === modelId);
  const isEligible = eligibleShortVariants.length > 0;

  const variantDetails = eligibleShortVariants.map(sv => {
    const details = getVariantDetails(modelId, sv);
    return { short: sv, details };
  }).filter(v => v.details);

  return (
    <div
      data-testid={`eligibility-card-${modelId}`}
      className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 bg-gradient-to-br ${
        isEligible ? t.eligible : t.notEligible
      }`}
    >
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3.5 md:px-5 md:py-4">
        <div className="flex items-center gap-3">
          {model && (
            <div className="w-14 h-10 md:w-16 md:h-12 flex-shrink-0 flex items-center justify-center">
              <img src={model.image} alt={modelDisplayNames[modelId]} className="max-h-10 md:max-h-12 w-auto object-contain" />
            </div>
          )}
          <div className="text-left">
            <h3 className={`font-bold text-base md:text-lg tracking-wide ${t.modelName}`}>
              {modelDisplayNames[modelId]}
            </h3>
            <div className={`flex items-center gap-1.5 mt-0.5 ${isEligible ? t.eligibleText : t.notEligibleText}`}>
              {isEligible ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              <span className="text-xs font-semibold uppercase tracking-wider">
                {isEligible ? 'Layak Mohon' : 'Belum Sesuai'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEligible && (
            <span className={`text-[10px] md:text-xs font-medium ${t.variantCount}`}>{eligibleShortVariants.length} varian</span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${t.chevron} ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 md:px-5 md:pb-5">
          {isEligible ? (
            <>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {eligibleShortVariants.map(sv => (
                  <span key={sv} className={`text-[10px] md:text-xs font-semibold px-2.5 py-1 rounded-full border ${t.eligibleChip}`}>{sv}</span>
                ))}
              </div>
              <div className="rounded-xl overflow-hidden border border-white/[0.06]">
                <div className={`grid grid-cols-3 px-3 py-2 ${t.tableHeader}`}>
                  <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider">Varian</span>
                  <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-right">Harga OTR</span>
                  <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-right">Ansuran/bln</span>
                </div>
                {variantDetails.map((v, idx) => (
                  <div key={v.short} className={`grid grid-cols-3 px-3 py-2.5 ${idx % 2 === 0 ? t.tableRow : ''}`}>
                    <span className={`text-xs md:text-sm font-medium ${t.tableText}`}>{v.details.name}</span>
                    <span className={`text-xs md:text-sm font-semibold text-right ${t.tablePrice}`}>{formatRM(v.details.price)}</span>
                    <span className={`text-xs md:text-sm font-bold text-right ${t.tableMonthly}`}>~{formatRM(v.details.fullLoan9)}</span>
                  </div>
                ))}
              </div>
              <p className={`text-[9px] mt-2 text-right ${t.tableNote}`}>*Ansuran 9 tahun, full loan, kadar anggaran</p>
            </>
          ) : (
            <p className={`text-sm italic ${t.notEligibleSub}`}>Gaji belum mencukupi untuk model ini.</p>
          )}
        </div>
      )}
    </div>
  );
};

// ──── MAIN ────
const EligibilityCalculator = () => {
  const navigate = useNavigate();
  const [salary, setSalary] = useState('');
  const [result, setResult] = useState(null);
  const [expandedModels, setExpandedModels] = useState({});
  const [themeKey, setThemeKey] = useState('blue');
  const resultRef = useRef(null);
  const t = themes[themeKey];

  const handleCheck = () => {
    const val = parseInt(salary, 10);
    if (!val || val <= 0) return;
    const eligible = getEligibleVariants(val);
    const grouped = {};
    allModelIds.forEach(id => { grouped[id] = []; });
    eligible.forEach(entry => {
      const parts = entry.split(' ');
      const modelId = parts[0].toLowerCase();
      const variantShort = parts.slice(1).join(' ');
      if (grouped[modelId]) grouped[modelId].push(variantShort);
    });
    const expanded = {};
    allModelIds.forEach(id => { expanded[id] = grouped[id].length > 0; });
    setResult({ salary: val, bracket: getBracket(val), grouped });
    setExpandedModels(expanded);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const toggleModel = (id) => setExpandedModels(prev => ({ ...prev, [id]: !prev[id] }));

  const eligibleModelNames = result
    ? allModelIds.filter(id => result.grouped[id]?.length > 0).map(id => modelDisplayNames[id])
    : [];

  const handleWhatsApp = () => {
    const salaryText = result ? ` Gaji bersih saya RM${result.salary.toLocaleString()}.` : '';
    window.open(`https://wa.me/60125006170?text=${encodeURIComponent(`Hi Syasyah, saya nak check kelayakan Perodua.${salaryText}`)}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${t.page}`} data-testid="eligibility-page">
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-md border-b transition-colors duration-500 ${t.header}`}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/')} data-testid="eligibility-back-btn" className={`flex items-center gap-1.5 transition-colors text-sm ${t.headerText}`}>
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Kembali</span>
          </button>
          <span className={`text-xs font-medium ${t.headerSub}`}>Syasyah Perodua</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        {/* Theme Selector */}
        <div className="flex justify-center mb-6">
          <ThemeSelector current={themeKey} onChange={setThemeKey} t={t} />
        </div>

        {/* Hero */}
        <div className="text-center mb-8 md:mb-10">
          <div className={`inline-flex items-center gap-1.5 border px-4 py-1.5 rounded-full text-xs font-semibold mb-5 ${t.badge}`}>
            <Search className="w-3.5 h-3.5" />
            Semakan Kelayakan
          </div>
          <h1 className={`text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight mb-3 ${t.h1}`}>
            KERETA PERODUA YANG{' '}
            <span className={`text-transparent bg-clip-text bg-gradient-to-r ${t.h1Accent}`}>LAYAK MOHON</span>{' '}
            IKUT GAJI
          </h1>
          <p className={`text-sm md:text-base max-w-lg mx-auto ${t.subtitle}`}>
            Masukkan gaji bersih anda, sistem akan cadangkan model & varian Perodua yang sesuai.
          </p>
        </div>

        {/* Calculator Card */}
        <div className={`backdrop-blur-sm border rounded-2xl p-5 md:p-7 mb-6 transition-colors duration-500 ${t.card}`} data-testid="salary-input-card">
          <label className={`block text-sm font-semibold mb-2 ${t.label}`}>Gaji Bersih Anda</label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-bold text-sm ${t.inputPrefix}`}>RM</span>
              <input
                type="number"
                data-testid="salary-input"
                value={salary}
                onChange={(e) => setSalary(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
                placeholder="Contoh 2000"
                className={`w-full rounded-xl pl-12 pr-4 py-3.5 text-lg font-semibold border focus:outline-none focus:ring-1 transition-all ${t.input}`}
              />
            </div>
            <Button onClick={handleCheck} data-testid="check-eligibility-btn" className={`px-5 md:px-7 py-3.5 rounded-xl text-sm transition-all whitespace-nowrap ${t.btn}`}>
              <Search className="w-4 h-4 mr-1.5" />
              Semak
            </Button>
          </div>
          <div className={`flex items-start gap-2 mt-3 text-[10px] md:text-xs ${t.note}`}>
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Untuk gaji RM1,500 ke RM2,400 sahaja. Jika gaji lebih tinggi, terus PM untuk semakan penuh.</span>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div ref={resultRef}>
            {result.bracket === null ? (
              <div className={`border rounded-2xl p-5 md:p-7 text-center ${t.belowMin}`} data-testid="below-minimum-msg">
                <XCircle className={`w-10 h-10 mx-auto mb-3 ${t.notEligibleText}`} />
                <h3 className={`font-bold text-lg mb-1 ${t.belowMinTitle}`}>Belum Cukup Syarat</h3>
                <p className={`text-sm ${t.belowMinSub}`}>
                  Gaji bersih RM{result.salary.toLocaleString()} belum mencukupi syarat anggaran awal (minimum RM1,500).
                </p>
                <p className={`text-xs mt-3 ${t.belowMinNote}`}>Hubungi Syasyah untuk perbincangan lanjut.</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className={`bg-gradient-to-r border rounded-2xl p-5 md:p-6 mb-6 ${t.summary}`} data-testid="eligibility-summary">
                  <p className={`text-sm mb-1 ${t.summaryText}`}>
                    Berdasarkan gaji <span className={`font-bold ${t.summaryBold}`}>RM{result.salary.toLocaleString()}</span> (bracket RM{result.bracket.toLocaleString()}), anda layak mohon anggaran untuk:
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {eligibleModelNames.map(name => (
                      <span key={name} className={`text-xs font-bold px-3 py-1.5 rounded-full border ${t.summaryChip}`}>{name}</span>
                    ))}
                  </div>
                </div>

                {/* Model Cards */}
                <div className="space-y-3" data-testid="model-results">
                  {allModelIds.map(id => (
                    <ModelResultCard key={id} modelId={id} eligibleShortVariants={result.grouped[id] || []} expanded={expandedModels[id] || false} onToggle={() => toggleModel(id)} t={t} />
                  ))}
                </div>
              </>
            )}

            {/* CTA */}
            <div className={`mt-8 bg-gradient-to-br border rounded-2xl p-5 md:p-7 text-center ${t.cta}`} data-testid="cta-section">
              <h3 className={`font-bold text-lg md:text-xl mb-2 ${t.ctaTitle}`}>Nak Syasyah check kelayakan sebenar?</h3>
              <p className={`text-xs md:text-sm max-w-md mx-auto mb-5 ${t.ctaSub}`}>
                Result ini anggaran awal sahaja. Untuk semakan sebenar, Syasyah boleh bantu check ikut gaji, komitmen dan dokumen anda.
              </p>
              <Button onClick={handleWhatsApp} data-testid="cta-whatsapp-btn" className={`px-8 py-4 rounded-xl text-sm md:text-base transition-all transform hover:scale-105 shadow-lg shadow-green-900/30 ${t.ctaBtn}`}>
                <Phone className="w-5 h-5 mr-2" />
                PM CHECK KELAYAKAN
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="mt-8 px-1">
          <p className={`text-[10px] md:text-xs leading-relaxed text-center ${t.disclaimer}`}>
            Anggaran ini bukan kelulusan rasmi. Kelulusan sebenar tertakluk kepada penilaian bank, komitmen bulanan, rekod CCRIS/CTOS, dokumen sokongan dan syarat semasa.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className={`border-t py-4 mt-8 ${t.footer}`}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className={`text-xs ${t.footerText}`}>Syasyah Aran - Perodua Sales Advisor | {advisorInfo.phone}</p>
        </div>
      </footer>
    </div>
  );
};

export default EligibilityCalculator;
